import { Component, OnInit } from '@angular/core';
import { DisplayFlattenUser, HEADERS, AccountStatus, TableColumnItem } from '../../models/users';
import { ApiService } from '../../../shared/services/api.service';
import {
  ListUsersMutation,
  ListUsersMutationVariables,
  UserActionsMutation,
  UserActionsMutationVariables,
  UserActions,
} from '../../../../API';
import { ListUsers, UserActions as UserActionsGraphQL } from '../../../../graphql/mutations';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AuthService } from '../../../shared/services/auth.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CreateUserComponent } from '../create-user/create-user.component';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-list-users',
  templateUrl: './list-users.component.html',
  styleUrl: './list-users.component.scss',
})
export class ListUsersComponent implements OnInit {
  isLoading: boolean = false;

  nameVisible: boolean = false;
  searchNameValue: string = '';

  emailVisible: boolean = false;
  searchEmailValue: string = '';

  totalCount: number = 0;
  supportedPageSizes: number[] = [10, 25, 50, 100];

  rowsToDisplay: DisplayFlattenUser[] = [];
  headers: TableColumnItem[] = HEADERS;
  userList: DisplayFlattenUser[] = [];

  isNonNFLUser: boolean = false;
  userActions = UserActions;
  loggedInUser!: string;

  readonly hasUserActionEntitlement: string[] = [
    'app.dcr-portal.user-management.action.delete',
    'app.dcr-portal.user-management.action.edit',
  ];

  constructor(
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    public authService: AuthService,
    private nzMessageService: NzMessageService,
    private nzModalService: NzModalService,
  ) {}

  ngOnInit(): void {
    this.getUserList();
    this.loggedInUser = this.userManagementService.getEmail();
  }

  getUserList(): void {
    this.isLoading = true;

    this.apiService
      .query<ListUsersMutation, ListUsersMutationVariables>(ListUsers, {
        userRequest: { orgType: this.userManagementService.getOrgType() },
      })
      .then((response) => {
        const userList = response.data?.ListUsers.Users;

        this.userList = (userList || []).map((user) => {
          const { Attributes, UserCreateDate, UserLastModifiedDate, Groups, Enabled } = user;
          const name = Attributes?.find((attr) => attr?.Name === 'name')?.Value;
          const email = Attributes?.find((attr) => attr?.Name === 'email')?.Value;
          const sub = Attributes?.find((attr) => attr?.Name === 'sub')?.Value;
          const organization = Attributes?.find((attr) => attr?.Name === 'custom:orgType')?.Value;
          const updatedAtStr = Attributes?.find((attr) => attr?.Name === 'updated_at')?.Value;
          const updatedAt = updatedAtStr ? Number(updatedAtStr) : null;
          const orgType =
            this.userManagementService.orgList?.find((org) => org?.orgName === organization)?.orgType || '';

          return {
            name,
            email,
            organization,
            role: this.filterAndGetRole(Groups),
            status: Enabled ? AccountStatus.True : AccountStatus.FALSE,
            createdAt: UserCreateDate,
            updatedAt: updatedAt,
            lastActivity: UserLastModifiedDate,
            orgType: orgType,
            sub: sub,
          };
        });
        this.isNonNFLUser = this.userManagementService.getOrgType() !== 'nfl';
        this.headers = this.isNonNFLUser ? HEADERS.filter((header) => header.key !== 'organization') : HEADERS;
        this.rowsToDisplay = [...this.userList!];
        this.totalCount = this.rowsToDisplay?.length;

        this.setRoleFilter();
        this.setOrgTypeFilter();
      })
      .catch((err) => {
        console.error(err);
        this.nzMessageService.error('Error fetching list of user');
      })
      .finally(() => (this.isLoading = false));
  }

  filterAndGetRole(groups: string[] | null | undefined) {
    const userGroups = groups?.filter((group: string) => !group.endsWith('Okta')) || [];
    return this.authService.groupRoleMap[userGroups[0]];
  }

  setRoleFilter(): void {
    const header = this.headers.find((head) => head.key === 'role');
    if (header) {
      header.listOfFilter = this.userList
        .map((record: DisplayFlattenUser) => record.role!)
        .filter((role, index, arr) => {
          return arr && role && arr.indexOf(role) === index;
        })
        .map((role) => {
          return { text: role, value: role };
        });
    }
  }

  setOrgTypeFilter(): void {
    const header = this.headers.find((head) => head.key === 'organization');
    if (header) {
      header.listOfFilter = this.userList
        .map((record: DisplayFlattenUser) => record.organization!)
        .filter((organization, index, arr) => arr && organization && arr.indexOf(organization) === index)
        .map((organization) => {
          return { text: organization, value: organization };
        });
    }
  }

  getUserStatusColor(status: string | null | undefined): string {
    switch (status) {
      case AccountStatus.True:
        return 'success';
      case AccountStatus.FALSE:
        return 'error';
      default:
        return '';
    }
  }

  searchName() {
    this.nameVisible = false;
    this.rowsToDisplay = this.userList.filter(
      (item: DisplayFlattenUser) =>
        item && item.name && item.name.toLowerCase().indexOf(this.searchNameValue.toLowerCase()) !== -1,
    );
  }

  resetName(): void {
    this.searchNameValue = '';
    this.searchName();
  }

  searchEmail(): void {
    this.emailVisible = false;
    this.rowsToDisplay = this.userList.filter(
      (item: DisplayFlattenUser) =>
        item && item.email && item.email.toLowerCase().indexOf(this.searchEmailValue.toLowerCase()) !== -1,
    );
  }

  resetEmail(): void {
    this.searchEmailValue = '';
    this.searchEmail();
  }

  openUserActionsModal(user: DisplayFlattenUser, action: UserActions): void {
    const actionText = action.toLowerCase().replace('_user', '');

    this.nzModalService.confirm({
      nzTitle: `Are you sure you want to ${actionText} this user?`,
      nzContent: `User will be ${actionText}d.`,
      nzOkText: `Yes, ${actionText}`,
      nzOnOk: () => this.handleUserAction(user, action, actionText),
      nzCancelText: 'Cancel',
    });
  }

  handleUserAction(user: DisplayFlattenUser, action: UserActions, actionText: string): void {
    this.isLoading = true;

    this.apiService
      .query<UserActionsMutation, UserActionsMutationVariables>(UserActionsGraphQL, {
        userRequest: { action: action, cognitoUserSub: user.sub! },
      })
      .then((response) => {
        if (response.data?.UserActions.success) {
          this.nzMessageService.success(`User ${user.name} has been ${actionText}d successfully`);
          this.getUserList();
        } else {
          this.nzMessageService.error(`Failed to perform action: ${response.data?.UserActions.message}`);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to perform action: ${error?.message || 'Unknown error'}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  openEditUserModal(user: DisplayFlattenUser): void {
    this.nzModalService.create({
      nzContent: CreateUserComponent,
      nzFooter: null,
      nzClosable: true,
      nzTitle: 'Update User',
      nzWidth: '50rem',
      nzData: user,
      nzOnOk: () => {
        this.getUserList();
      },
    });
  }
}
